import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import { faCircleExclamation, faTrashAlt, faUserGroup } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { canEditContent } from "userful-chronos-app-common-js/dist/authenticataion/roles";
import { getGlobalStates } from "userful-chronos-app-common-js/dist/globalstates/globalStates";
import { NOT_SET_VALUE, StringID, UserfulSecurityData } from "userful-chronos-app-common-js/dist/models/common";
import {
    requestRemoveUConductWorkspace,
    requestUpdateUconductWorkspaceUserfulSecurityData,
} from "userful-chronos-app-common-js/dist/models/uconduct/messages/uConductViewMsgSender";
import { UConductWorkspaceComp } from "userful-chronos-app-common-js/dist/models/uconduct/View";
import { UserPermission } from "userful-chronos-app-common-js/dist/models/user-permission";
import { KeycloakUserIDName } from "userful-chronos-app-common-js/dist/models/usermgt/usermgt";
import { useUsermgtSelector } from "userful-chronos-common-store/dist/usermgt";
import Button from "../../button/Button";
import UserfulConfirmationDialog from "../../ConfirmationDialog/UserfulConfirmationDialog";
import Dropdown from "../../dropdown/Dropdown";
import { IDropdownItem } from "../../dropdown/dropdownUtils";
import "./SourceActionsMenu.scss";

interface IProps {
    value: UConductWorkspaceComp;
}

export default function UConductWorkspaceMenu({ value }: IProps) {
    const { t } = useTranslation();
    const userPermission = getGlobalStates().userPermission as UserPermission;
    const canEdit = canEditContent(value.userfulSecurityData);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const allUsers = useUsermgtSelector((state) => state.usermgtStore.usernameIDs) as KeycloakUserIDName[];

    // const dispatch = useSourceAssetDispatch();
    const onSave = (id: StringID, data: UserfulSecurityData) => {
        const payload = {
            id: id,
            userfulSecurityData: { ...data, id: id },
        };
        requestUpdateUconductWorkspaceUserfulSecurityData(payload);
    };

    const canSetOwner =
        userPermission.canOverrideSecurityDataOwnerShip &&
        value.userfulSecurityData.clearanceLevelPermissions.length > 0;

    const ownerItems: IDropdownItem[] = useMemo(() => {
        if (!canSetOwner) return [];
        return allUsers.map((user) => ({
            id: `owner-${user.userID.value}`,
            title: user.userName,
            options: "radio",
            value: user.userName === value.userfulSecurityData.userName ? "selected" : "unselected",
            doNotCloseOnClick: true,
        }));
    }, [allUsers, canSetOwner, value.userfulSecurityData.userID]);

    const dropdownItems: IDropdownItem[] = useMemo(() => {
        const items: IDropdownItem[] = [];

        if (canSetOwner && ownerItems.length > 0) {
            items.push({
                id: "set-owner",
                title: "Shared.SetOwner",
                icon: <FontAwesomeIcon icon={faUserGroup} />,
                children: ownerItems,
                doNotCloseOnClick: true,
            });
        }

        if (canEdit && !userPermission.isUConductOperator) {
            items.push({
                id: "delete-workspace",
                title: "Shared.delete",
                icon: <FontAwesomeIcon icon={faTrashAlt} />,
            });
        }

        return items;
    }, [canEdit, canSetOwner, ownerItems, userPermission.isUConductOperator]);

    if (dropdownItems.length === 0) return <></>;

    const handleDropdownChange = (item: IDropdownItem) => {
        if (item.id.startsWith("owner-")) {
            const selectedUserID = item.id.replace("owner-", "");
            const selectedUser = allUsers.find((user) => user.userID.value === selectedUserID);
            const isSelected = item.value === "selected";
            const updatedSecurityData: UserfulSecurityData = {
                ...value.userfulSecurityData,
                userID: isSelected && selectedUser ? selectedUser.userID.value : NOT_SET_VALUE,
                userName: isSelected && selectedUser ? selectedUser.userName : NOT_SET_VALUE,
            };
            onSave(value.id, updatedSecurityData);
            return;
        }

        if (item.id === "delete-workspace") {
            setShowDeleteConfirmation(true);
        }
    };

    return (
        <>
            <div onClick={(event) => event.stopPropagation()}>
                <Dropdown
                    items={dropdownItems}
                    onChange={handleDropdownChange}
                    usePortal
                    trigger={
                        <Button className="source-actions-menu-toggle" variant="ghost" iconOnly>
                            <FontAwesomeIcon icon={faEllipsisV} />
                        </Button>
                    }
                />
            </div>

            <UserfulConfirmationDialog
                show={showDeleteConfirmation}
                icon={{ icon: faCircleExclamation, color: "#D13E2C" }}
                onCancel={() => setShowDeleteConfirmation(false)}
                title={`${t("Shared.delete")} ${t("Shared.workspace")}`}
                replaceBody={
                    <p>
                        {t("Shared.deleteStart")} <strong>{value.name}</strong>
                        {"? "}
                        {t("Shared.deleteEnd")}
                    </p>
                }
                replaceFooter={
                    <>
                        <Button variant="secondary" onClick={() => setShowDeleteConfirmation(false)}>
                            {t("Shared.cancel")}
                        </Button>
                        <Button
                            variant="danger"
                            onClick={() => {
                                requestRemoveUConductWorkspace(value.id);
                                setShowDeleteConfirmation(false);
                            }}
                        >
                            {t("Shared.delete")}
                        </Button>
                    </>
                }
            />
        </>
    );
}
